import { ValidationError } from "sequelize"
import {
    OkrKeyResult,
    OkrKeyResultCreate,
    OkrKeyResultSave,
    OkrKeyResultSaveResult,
    OkrKeyResultUpdateDeadline,
    OkrKeyResultUpdateActualNumber,
    OkrKeyResultMove,
    OkrKeyResultOwner,
    SaveOkrKeyResultOwnerType,
    OkrKeyResultTag,
    SaveOkrKeyResultTagType,
} from "server/models"
import { GET, POST, DELETE, validate } from "client/store/utils"
import { LangType } from "client/store"
import {
    OkrKeyResultList,
    OkrKeyResultIds,
    OkrKeyResultValidation,
} from "./types"


export const LoadAllOkrKeyResults = (sessionId: number) => (
    GET(`/okrKeyResult/sessionId/${sessionId}`)
    .then((keyResults: OkrKeyResult[]) => {
        const keyResultList: OkrKeyResultList = {}
        const keyResultIds: OkrKeyResultIds = {}
        keyResults.forEach((keyResult) => {
            keyResultList[keyResult.id] = keyResult
            if (!keyResultIds[keyResult.objectiveId]) keyResultIds[keyResult.objectiveId] = []
            const krIds: number[] = keyResultIds[keyResult.objectiveId].slice()
            krIds.push(keyResult.id)
            keyResultIds[keyResult.objectiveId] = krIds
        })
        return { keyResultList, keyResultIds, keyResults }
    })
)

export const LoadOkrObjectiveKeyResults = (objectiveId: number, sessionId: number) => (
    GET(`/okrKeyResult/objectiveId/${objectiveId}/sessionId/${sessionId}`)
    .then((keyResults: OkrKeyResult[]) => {
        const keyResultList: OkrKeyResultList = {}
        const keyResultIds: number[] = []
        keyResults.forEach((keyResult) => {
            keyResultList[keyResult.id] = keyResult
            keyResultIds.push(keyResult.id)
        })
        return { keyResultList, keyResultIds }
    })
)

export const LoadOkrKeyResultById = (
    keyResultId: number,
    objectiveId: number,
    sessionId: number,
) => (
    GET(`/okrKeyResult/id/${keyResultId}/objectiveId/${objectiveId}/sessionId/${sessionId}`)
    .then((keyResult: OkrKeyResult) => keyResult)
)

export const SaveOkrKeyResult = (
    id: number,
    keyResult: OkrKeyResultCreate,
    ownerIds: number[],
    tagIds: number[],
    lang: LangType,
) => {
    const data: OkrKeyResultSave = {
        id,
        keyResult,
    }
    return POST(
        "/okrKeyResult/save",
        JSON.stringify(data),
    ).then(({ response, order }: OkrKeyResultSaveResult) => {
        const newKeyResult = response as OkrKeyResult
        const error = response as ValidationError

        const newKeyResultId = newKeyResult.id || null
        const validation = <OkrKeyResultValidation>validate(error, lang)

        if (validation) {
            return {
                newKeyResultId,
                validation,
                newOrder: order,
                owners: null,
                tags: null,
            }
        }

        const owners: OkrKeyResultOwner[] = []
        ownerIds.forEach((userId) => {
            owners.push({
                keyResultId: newKeyResultId || id,
                userId,
            })
        })

        const tags: OkrKeyResultTag[] = []
        tagIds.forEach((tagId) => {
            tags.push({
                keyResultId: newKeyResultId || id,
                tagId,
            })
        })

        return Promise.all([
            SaveOkrKeyResultOwners(newKeyResultId || id, owners),
            SaveOkrKeyResultTags(newKeyResultId || id, tags),
        ]).then(() => ({
            newKeyResultId,
            validation,
            newOrder: order,
            owners,
            tags,
        }))
    })
}

export const UpdateOkrKeyResultSoftDeadline = (data: OkrKeyResultUpdateDeadline) => (
    POST(
        "/okrKeyResult/updateSoftDeadline",
        JSON.stringify(data),
    )
)

export const UpdateOkrKeyResultHardDeadline = (data: OkrKeyResultUpdateDeadline) => (
    POST(
        "/okrKeyResult/updateHardDeadline",
        JSON.stringify(data),
    )
)

export const UpdateOkrKeyResultActualNumber = (data: OkrKeyResultUpdateActualNumber) => (
    POST(
        "/okrKeyResult/updateActualNumber",
        JSON.stringify(data),
    )
)

export const MoveOkrKeyResult = (data: OkrKeyResultMove) => (
    POST(
        "/okrKeyResult/move",
        JSON.stringify(data),
    )
)

export const DragnDropOkrKeyResult = (
    keyResultIds: readonly number[],
    keyResultList: OkrKeyResultList,
) => {
    const data: OkrKeyResult[] = []
    keyResultIds.forEach((id, index) => {
        data.push({
            ...keyResultList[id],
            order: index,
        })
    })

    return POST(
        "/okrKeyResult/updateMany",
        JSON.stringify(data),
    ).then((success: boolean) => success)
}

export const IsOwnerOkrKeyResult = (keyResult: OkrKeyResult, userId: number) => {
    let isOwner = false
    if (keyResult && keyResult.owners) {
        keyResult.owners.forEach((row) => {
            if (row.userId === userId) isOwner = true
        })
    }
    return isOwner
}

export const DeleteOkrKeyResult = (
    keyResultId: number,
    objectiveId: number,
    sessionId: number,
) => (
    DELETE(`/okrKeyResult/delete/${keyResultId}/${objectiveId}/${sessionId}`)
    .then((removedRows: number) => removedRows)
)

export const SaveOkrKeyResultOwners = (
    keyResultId: number,
    owners: OkrKeyResultOwner[],
) => {
    const ownerData: SaveOkrKeyResultOwnerType = {
        keyResultId,
        owners,
    }
    return POST(
        "/okrKeyResultOwner/save",
        JSON.stringify(ownerData),
    )
}

export const SaveOkrKeyResultTags = (
    keyResultId: number,
    tags: OkrKeyResultTag[],
) => {
    const tagData: SaveOkrKeyResultTagType = {
        keyResultId,
        tags,
    }
    return POST(
        "/okrKeyResultTag/save",
        JSON.stringify(tagData),
    )
}
